/**
 * Preview-only mock harness.
 *
 * Gated by BOTH `import.meta.env.DEV` AND `VITE_MOCK_AUTH === "1"`.
 * In production builds Vite statically replaces `import.meta.env.DEV` with
 * `false`, collapsing every `MOCK_MODE` branch to dead code that Rollup
 * tree-shakes from the final bundle. We additionally grep for the
 * MOCK_FINGERPRINT sentinel after build to prove the harness didn't ship.
 *
 * IMPORTANT: Never reference these fixtures from a code path that runs in
 * production. The whole point is they vanish from the prod bundle.
 */

import type { User } from "firebase/auth";
import { ApiError } from "./api.ts";
import type {
  AdminCommunitySummary,
  ContentSnapshot,
  Listing,
  Pagination as PageMeta,
  Report,
  Restaurant,
  SafetyNote,
} from "./types.ts";

// Tree-shake-friendly constant. With DEV=false in prod builds this evaluates
// to `false && ...` → `false`, and every guarded branch is eliminated.
export const MOCK_MODE: boolean =
  import.meta.env.DEV === true &&
  (import.meta.env.VITE_MOCK_AUTH as string | undefined) === "1";

// Sentinel string we can grep for in the production dist to confirm the
// harness was not shipped. If grep finds this in prod assets, tree-shaking
// failed and the build is unsafe to ship.
export const MOCK_FINGERPRINT = "DIALECTO_MOCK_HARNESS_FINGERPRINT_v1";

// Fake admin user — only constructed when MOCK_MODE is true at runtime.
// In prod the `false ? {...} : null` collapses to `null` and the literal
// object is never instantiated.
export const mockUser = (MOCK_MODE
  ? ({
      email: "preview@expat-admin.test",
      uid: "preview-admin-uid",
      emailVerified: true,
      getIdToken: async () => "preview-token",
    } as unknown as User)
  : null) as User | null;

/* ============================================================
   Fixtures
   ============================================================ */

const COMMUNITIES: AdminCommunitySummary[] = [
  {
    id: "1",
    slug: "merida",
    name: "Mérida",
    active: true,
    createdAt: "2025-02-14",
    pendingRestaurantCount: 4,
    openReportCount: 7,
  },
  {
    id: "2",
    slug: "playa",
    name: "Playa del Carmen",
    active: true,
    createdAt: "2025-03-02",
    pendingRestaurantCount: 1,
    openReportCount: 3,
  },
  {
    id: "3",
    slug: "oaxaca",
    name: "Oaxaca City",
    active: true,
    createdAt: "2025-04-19",
    pendingRestaurantCount: 0,
    openReportCount: 2,
  },
  {
    id: "4",
    slug: "guadalajara",
    name: "Guadalajara",
    active: false,
    createdAt: "2025-05-08",
    pendingRestaurantCount: 0,
    openReportCount: 0,
  },
];

const REPORTS_OPEN: Report[] = [
  {
    id: "8821",
    communityId: "1",
    targetType: "listing",
    targetId: "4011",
    reporterUserId: "8821",
    reason: "spam",
    detail: "Posted 7 nearly-identical real estate ads in 24h.",
    resolved: false,
    createdAt: "2026-06-07 14:21",
    reporter: { displayName: "Ana Hernández", priorReportCount: 0 },
  },
  {
    id: "8819",
    communityId: "2",
    targetType: "safety_note",
    targetId: "1207",
    reporterUserId: "44",
    reason: "harassment",
    detail: "Names an individual; tone is accusatory.",
    resolved: false,
    createdAt: "2026-06-07 11:02",
    reporter: { displayName: "Marco Lerma", priorReportCount: 4 },
  },
  {
    id: "8814",
    communityId: "1",
    targetType: "listing",
    targetId: "3998",
    reporterUserId: "1129",
    reason: "scam",
    detail: "WhatsApp number routes to a known fraud ring.",
    resolved: false,
    createdAt: "2026-06-06 22:48",
    reporter: { displayName: null, priorReportCount: 1 },
  },
  {
    id: "8808",
    communityId: "3",
    targetType: "user",
    targetId: "5572",
    reporterUserId: "991",
    reason: "impersonation",
    detail: "Claims to be Isaac. Account is 2 days old.",
    resolved: false,
    createdAt: "2026-06-06 09:11",
    reporter: { displayName: "Sofía R.", priorReportCount: 0 },
  },
  {
    id: "8803",
    communityId: "1",
    targetType: "listing",
    targetId: "3992",
    reporterUserId: "1129",
    reason: "wrong_category",
    detail: "Listed under 'restaurants' but it's a salon.",
    resolved: false,
    createdAt: "2026-06-05 18:33",
    reporter: { displayName: null, priorReportCount: 1 },
  },
];

const REPORTS_RESOLVED: Report[] = [
  {
    id: "8790",
    communityId: "1",
    targetType: "listing",
    targetId: "3941",
    reporterUserId: "229",
    reason: "spam",
    detail: "Cross-posted from a Telegram bot.",
    resolved: true,
    createdAt: "2026-06-03 16:04",
    reporter: { displayName: "Carla M.", priorReportCount: 2 },
  },
  {
    id: "8782",
    communityId: "2",
    targetType: "safety_note",
    targetId: "1185",
    reporterUserId: "11",
    reason: "outdated",
    detail: "Construction the note warned about ended last month.",
    resolved: true,
    createdAt: "2026-06-02 10:51",
    reporter: { displayName: null, priorReportCount: 0 },
  },
];

const RESTAURANTS: Restaurant[] = [
  {
    id: "412",
    communityId: "1",
    ownerUserId: "1129",
    status: "pending",
    name: "La Chaya Maya",
    whatsappNumber: "+52 999 142 8482",
    hours: "Mon–Sun 8:00–22:30",
    info: "Yucatecan classics in a converted casona. House special: cochinita pibil.\nNew menu items added Apr 2026.",
    menuImageUrls: [],
    menuImageUrl: null,
    parentRestaurantId: null,
    createdAt: "2026-06-04",
    updatedAt: "2026-06-07",
    menuItems: [
      { id: "1", restaurantId: "412", nameEn: "Cochinita Pibil", nameEs: "Cochinita Pibil", price: "$185 MXN", category: "mains", note: null, position: 1 },
      { id: "2", restaurantId: "412", nameEn: "Sopa de Lima", nameEs: "Sopa de Lima", price: "$95 MXN", category: "soups", note: null, position: 2 },
      { id: "3", restaurantId: "412", nameEn: "Papadzules", nameEs: "Papadzules", price: "$130 MXN", category: "mains", note: null, position: 3 },
      { id: "4", restaurantId: "412", nameEn: "Horchata", nameEs: "Horchata", price: "$55 MXN", category: "drinks", note: null, position: 4 },
    ],
  },
  {
    id: "411",
    communityId: "1",
    ownerUserId: "990",
    status: "pending",
    name: "Café Crema",
    whatsappNumber: "+52 999 211 5599",
    hours: "Tue–Sun 7:30–14:00",
    info: "Specialty coffee bar in Santiago. Small-batch roasters from Chiapas.",
    menuImageUrls: [],
    menuImageUrl: null,
    parentRestaurantId: null,
    createdAt: "2026-06-04",
    updatedAt: "2026-06-04",
    menuItems: [
      { id: "5", restaurantId: "411", nameEn: "Flat White", nameEs: "Flat White", price: "$58 MXN", category: "coffee", note: null, position: 1 },
      { id: "6", restaurantId: "411", nameEn: "Cold Brew", nameEs: "Café Frío", price: "$72 MXN", category: "coffee", note: null, position: 2 },
    ],
  },
  {
    id: "402",
    communityId: "2",
    ownerUserId: "1201",
    status: "approved",
    name: "El Fogón",
    whatsappNumber: "+52 984 803 1010",
    hours: "Mon–Sun 12:00–23:00",
    info: "Tacos al pastor and sopes. Open since 2018.",
    menuImageUrls: [],
    menuImageUrl: null,
    parentRestaurantId: null,
    createdAt: "2025-09-12",
    updatedAt: "2026-05-20",
    menuItems: [
      { id: "7", restaurantId: "402", nameEn: "Pastor Taco", nameEs: "Taco al Pastor", price: "$28 MXN", category: "tacos", note: null, position: 1 },
      { id: "8", restaurantId: "402", nameEn: "Sope de Chorizo", nameEs: "Sope de Chorizo", price: "$48 MXN", category: "antojitos", note: null, position: 2 },
    ],
  },
  {
    id: "388",
    communityId: "1",
    ownerUserId: "812",
    status: "rejected",
    name: "Sushi Niko",
    whatsappNumber: "+52 999 555 0011",
    hours: "—",
    info: "Owner submitted a stub with no menu items and no address.",
    menuImageUrls: [],
    menuImageUrl: null,
    parentRestaurantId: null,
    createdAt: "2026-05-01",
    updatedAt: "2026-05-02",
    menuItems: [],
  },
];

const LISTINGS: ContentSnapshot[] = [
  {
    kind: "listing",
    id: "4011",
    communityId: "1",
    title: "1BR apartment near Centro — $14,500 MXN/mo",
    body: "Renovated. Unfurnished. WiFi, gas, water included. 12-month lease.",
    authorUserId: "8821",
    hidden: false,
    createdAt: "2026-06-07",
  },
  {
    kind: "listing",
    id: "3998",
    communityId: "1",
    title: "Bilingual nanny available weekdays",
    body: "Available Mon–Fri 7am–4pm. Eight years experience.",
    authorUserId: "1129",
    hidden: false,
    createdAt: "2026-06-06",
  },
  {
    kind: "listing",
    id: "3992",
    communityId: "1",
    title: "Salon Niko — opening soon",
    body: "Mistakenly filed under restaurants. Please move category.",
    authorUserId: "1129",
    hidden: true,
    createdAt: "2026-06-05",
  },
  {
    kind: "listing",
    id: "3989",
    communityId: "2",
    title: "Used Honda CB500 — runs great",
    body: "2019, 18k km. Includes helmet.",
    authorUserId: "1201",
    hidden: false,
    createdAt: "2026-06-05",
  },
  {
    kind: "listing",
    id: "3941",
    communityId: "1",
    title: "Spanish lessons — Skype + in-person",
    body: "Conversation-first. CEFR-aligned. First class free.",
    authorUserId: "229",
    hidden: false,
    createdAt: "2026-06-03",
  },
];

const SAFETY_NOTES: ContentSnapshot[] = [
  {
    kind: "safety_note",
    id: "1207",
    communityId: "2",
    title: "Avoid the Quinta Avenida ATM after 23:00",
    body: "Three skimmer reports in the last fortnight. Use the bank lobby ATMs nearby instead.",
    authorUserId: "44",
    hidden: false,
    createdAt: "2026-06-07",
  },
  {
    kind: "safety_note",
    id: "1199",
    communityId: "1",
    title: "Construction zone on Calle 60",
    body: "Sidewalk closed both sides between 41 and 47. Use Calle 62 if walking with strollers.",
    authorUserId: "11",
    hidden: false,
    createdAt: "2026-06-05",
  },
  {
    kind: "safety_note",
    id: "1185",
    communityId: "2",
    title: "Heads-up: closed for repairs (OUTDATED)",
    body: "Section reopened. Mods, please hide this note.",
    authorUserId: "11",
    hidden: true,
    createdAt: "2026-06-02",
  },
];

const LISTING_ROWS: Listing[] = [
  {
    id: "4011",
    communityId: "1",
    category: "transport",
    title: "Daily van: Mérida → Progreso",
    body: "Departs Plaza Grande 07:00 weekdays. 90 MXN one-way. Returns 18:00.",
    structured: { departure_point: "Plaza Grande", time: "07:00" },
    authorUserId: "admin-1",
    hidden: false,
    createdAt: "2026-06-07",
  },
  {
    id: "3998",
    communityId: "1",
    category: "delivery",
    title: "Bilingual veggie box delivery",
    body: "Weekly organic veggie box. WhatsApp +52 999…",
    structured: { delivery_window: "Wed AM" },
    authorUserId: "admin-1",
    hidden: false,
    createdAt: "2026-06-06",
  },
];

const SAFETY_NOTE_ROWS: SafetyNote[] = [
  {
    id: "1207",
    communityId: "2",
    category: "scam_pattern",
    patternTitle: "Quinta Avenida skimmer at the ATM",
    patternDescription: "Three reports in two weeks of skimmers installed on the Avenue 5 standalone ATM. Use bank-lobby ATMs.",
    placeDescription: "Standalone ATM at Quinta Avenida & Calle 14",
    structured: { time_of_day: "after_dark" },
    authorUserId: "admin-1",
    hidden: false,
    createdAt: "2026-06-07",
  },
];

/* ============================================================
   Mock dispatcher
   ============================================================ */

function paginate<T>(
  items: T[],
  limit: number,
  offset: number,
): { slice: T[]; total: number } {
  return { slice: items.slice(offset, offset + limit), total: items.length };
}

function paramsOf(path: string): URLSearchParams {
  const q = path.indexOf("?");
  return q === -1 ? new URLSearchParams() : new URLSearchParams(path.slice(q + 1));
}

function pathnameOf(path: string): string {
  const q = path.indexOf("?");
  return q === -1 ? path : path.slice(0, q);
}

// Preview-harness scenario switch. Lets a screenshot driver flip the AI-assist
// endpoints between their states (resolved / candidates / not_configured) by
// setting e.g. sessionStorage["mock_place"] = "candidates" before triggering a
// fetch. Dev-only — this whole module is tree-shaken from prod builds.
function mockScenario(key: string): string | null {
  try {
    return typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

export async function mockApi<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // Touch options so TS doesn't complain about unused param.
  void options.method;
  const method = (options.method ?? "GET").toUpperCase();
  // Simulate network so spinners render briefly.
  await new Promise((r) => setTimeout(r, 80));

  const p = pathnameOf(path);
  const sp = paramsOf(path);
  const community = sp.get("community_id");
  const limit = Number(sp.get("limit") ?? "25");
  const offset = Number(sp.get("offset") ?? "0");

  // /admin/communities ------------------------------------------------
  if (p === "/admin/communities") {
    return { items: COMMUNITIES } as unknown as T;
  }

  // /admin/reports ----------------------------------------------------
  if (p === "/admin/reports") {
    const pool =
      sp.get("resolved") === "true" ? REPORTS_RESOLVED : REPORTS_OPEN;
    const filtered = community
      ? pool.filter((r) => r.communityId === community)
      : pool;
    const { slice, total } = paginate(filtered, limit, offset);
    const meta: PageMeta = { total, limit, offset };
    return { items: slice, pagination: meta } as unknown as T;
  }
  if (/^\/admin\/reports\/.+\/(reopen|resolve)$/.test(p)) {
    return {} as T;
  }

  // /admin/restaurants ------------------------------------------------
  if (p === "/admin/restaurants") {
    if (method === "POST") {
      return RESTAURANTS[0] as unknown as T;
    }
    const status = sp.get("status");
    let filtered = community
      ? RESTAURANTS.filter((r) => r.communityId === community)
      : RESTAURANTS.slice();
    if (status) filtered = filtered.filter((r) => r.status === status);
    const { slice, total } = paginate(filtered, limit, offset);
    return { items: slice, total } as unknown as T;
  }
  if (/^\/admin\/restaurants\/.+\/(approve|reject|hide|unhide)$/.test(p)) {
    return {} as T;
  }
  const restMatch = p.match(/^\/admin\/restaurants\/([^/]+)$/);
  if (restMatch) {
    if (method === "DELETE") return {} as T;
    const id = decodeURIComponent(restMatch[1] ?? "");
    const found = RESTAURANTS.find((r) => r.id === id) ?? RESTAURANTS[0]!;
    return found as unknown as T;
  }

  // /admin/listings ---------------------------------------------------
  if (p === "/admin/listings") {
    if (method === "POST") return LISTING_ROWS[0] as unknown as T;
    const filtered = community
      ? LISTINGS.filter((l) => l.communityId === community)
      : LISTINGS.slice();
    const { slice, total } = paginate(filtered, limit, offset);
    return { items: slice, total } as unknown as T;
  }
  const listMatch = p.match(/^\/admin\/listings\/([^/]+)$/);
  if (listMatch) {
    if (method === "DELETE") return {} as T;
    const id = decodeURIComponent(listMatch[1] ?? "");
    const found =
      LISTING_ROWS.find((l) => l.id === id) ?? LISTING_ROWS[0]!;
    return found as unknown as T;
  }

  // /admin/safety-notes ----------------------------------------------
  if (p === "/admin/safety-notes") {
    if (method === "POST") return SAFETY_NOTE_ROWS[0] as unknown as T;
    const filtered = community
      ? SAFETY_NOTES.filter((n) => n.communityId === community)
      : SAFETY_NOTES.slice();
    const { slice, total } = paginate(filtered, limit, offset);
    return { items: slice, total } as unknown as T;
  }
  const noteMatch = p.match(/^\/admin\/safety-notes\/([^/]+)$/);
  if (noteMatch) {
    if (method === "DELETE") return {} as T;
    const id = decodeURIComponent(noteMatch[1] ?? "");
    const found =
      SAFETY_NOTE_ROWS.find((n) => n.id === id) ?? SAFETY_NOTE_ROWS[0]!;
    return found as unknown as T;
  }

  // /admin/content/{listing|safety-note}/{id}  + hide/unhide ----------
  const contentMatch = p.match(
    /^\/admin\/content\/(listing|safety-note)\/([^/]+)(?:\/(hide|unhide))?$/,
  );
  if (contentMatch) {
    const slug = contentMatch[1];
    const id = decodeURIComponent(contentMatch[2] ?? "");
    if (contentMatch[3]) return {} as T;
    const pool: ContentSnapshot[] =
      slug === "listing" ? LISTINGS : SAFETY_NOTES;
    const item = pool.find((x) => x.id === id) ?? pool[0]!;
    const relatedReports = REPORTS_OPEN.filter(
      (r) =>
        r.targetId === item.id &&
        (slug === "listing"
          ? r.targetType === "listing"
          : r.targetType === "safety_note"),
    );
    return { content: item, reports: relatedReports } as unknown as T;
  }

  // /admin/parse-menu (AI menu draft) ---------------------------------
  // Scenario is driven by sessionStorage["mock_parse"] so the preview
  // harness can deterministically show every state for screenshots:
  //   "not_configured" → 503 graceful banner
  //   (default)        → a parsed DRAFT with warnings
  if (p === "/admin/parse-menu" && method === "POST") {
    if (mockScenario("mock_parse") === "not_configured") {
      throw new ApiError("ai_not_configured", 503);
    }
    return {
      items: [
        { name_es: "Ceviche Mixto", name_en: "Mixed Ceviche", price: 220, category: "ceviches", note: null, warning: null },
        { name_es: "Encebollado", name_en: "Fish & Onion Soup", price: 95, category: "soups", note: "House special", warning: null },
        { name_es: "Arroz con Mariscos", name_en: "Seafood Rice", price: 180, category: "mains", note: null, warning: "Price digit smudged — read as 180 but verify." },
        { name_es: "Jugo de Maracuyá", name_en: "Passionfruit Juice", price: null, category: "drinks", note: null, warning: "Price was unreadable in the photo — left blank for you to fill in." },
      ],
      // Document-level only — per-row caveats now ride on each item's `warning`.
      warnings: [
        "Some category labels were inferred from the menu layout and may need review.",
      ],
    } as unknown as T;
  }

  // /admin/resolve-place (Google Places draft) ------------------------
  // Scenario via sessionStorage["mock_place"] OR a token in the URL so the
  // field self-demos:
  //   "not_configured"           → 503 graceful banner
  //   "candidates" / url~candidat → top-3 candidate picker
  //   (default)                  → a clean resolved pre-fill
  if (p === "/admin/resolve-place" && method === "POST") {
    const scenario = mockScenario("mock_place");
    if (scenario === "not_configured") {
      throw new ApiError("places_not_configured", 503);
    }
    let bodyUrl = "";
    try {
      bodyUrl = String(
        (JSON.parse(String(options.body ?? "{}")) as { maps_url?: string })
          .maps_url ?? "",
      );
    } catch {
      bodyUrl = "";
    }
    if (scenario === "candidates" || /candidat/i.test(bodyUrl)) {
      return {
        candidates: [
          { name: "Cevichería Casa Linda", formatted_address: "Av. Malecón 123, Tarqui, Manta, Ecuador", place_id: "cand-1", lat: -0.9676, lng: -80.7089 },
          { name: "Cevichería Casa Linda (Centro)", formatted_address: "Calle 10 y Av. 4, Centro, Manta, Ecuador", place_id: "cand-2", lat: -0.9512, lng: -80.7321 },
          { name: "Casa Linda Marisquería", formatted_address: "Barbasquillo, Manta, Ecuador", place_id: "cand-3", lat: -0.9802, lng: -80.7444 },
        ],
      } as unknown as T;
    }
    return {
      resolved: {
        name: "Cevichería Casa Linda",
        formatted_address: "Av. Malecón 123, Tarqui, Manta, Ecuador",
        opening_hours: {
          weekday_text: [
            "Monday: 11:00 AM – 9:00 PM",
            "Tuesday: 11:00 AM – 9:00 PM",
            "Wednesday: 11:00 AM – 9:00 PM",
            "Thursday: 11:00 AM – 9:00 PM",
            "Friday: 11:00 AM – 10:00 PM",
            "Saturday: 10:00 AM – 10:00 PM",
            "Sunday: 10:00 AM – 6:00 PM",
          ],
          periods: [],
          open_now: true,
        },
        formatted_phone_number: "+593 5 262 0000",
        lat: -0.9676,
        lng: -80.7089,
        place_id: "resolved-1",
      },
    } as unknown as T;
  }

  throw new Error(`mock: unhandled path "${p}"`);
}
